import React from 'react';
import { Typography } from '@one-thd/sui-atomic-components';
import { dgcClient as client } from './axios-client';
import { SURVEYS_TYPE, typeMap } from '../components/tableData';
import {
  DESKTOP,
  EMAIL_CONFIRMATION,
  ERECEIPT_SUBSCRIBE_SUCCESS,
  ERECEIPT_UNSUBSCRIBE_SUCCESS,
  ERECEIPTS,
  MARKETING_PREF,
  MOBILE,
  NO,
  PENDING,
  SMS_CONFIRMATION,
  TABLET,
  YES
} from '../constants';
import { isB2CUser } from '../../utils/general';
import { formatPhone } from '../components/common/helper';

let sourceId = null;

export function setSourceId(channel) {
  switch (channel) {
  case 'mobile':
    sourceId = MOBILE;
    break;
  case 'tablet':
    sourceId = TABLET;
    break;
  case 'desktop':
  default:
    sourceId = DESKTOP;
  }
}

export function getSourceId() {
  return `${isB2CUser() ? 'B2C' : 'B2B'}_${sourceId}`;
}

export const axiosPost = ({ url, data, callback, err }) => {
  client
    .post(url, data)
    .then(callback)
    .catch(err);
};

export const axiosPut = ({ url, data, callback, err }) => {
  client
    .put(url, data)
    .then(callback)
    .catch(err);
};

function checkStatus(preferences) {
  const email = preferences.find((item) => item.type === EMAIL_CONFIRMATION).status;
  const sms = preferences.find((item) => item.type === SMS_CONFIRMATION).status;
  if (sms === YES && email === YES) {
    return 'both';
  } if (sms === NO && email === YES) {
    return 'email';
  } if (sms === YES && email === NO) {
    return 'text';
  }
  return null;
}

export function setupHandleOrderCommunicationsRetrieve(setSelected, setInitial, managedAccount) {
  return (res) => {
    const preferences = res.data?.preferences;
    if (preferences.length > 0 && managedAccount) {
      const status = checkStatus(preferences);
      setInitial(status);
      setSelected(status);
    } else {
      setSelected('email');
      setInitial('email');
    }
  };
}

export function setupHandleLangRetrieve(tableData, setTableData, setSelected, setInitialSubs) {
  return (res) => {
    const lang = res.data?.langPref;
    setInitialSubs(lang);
  };
}

export function setupHandleEmailOrSmsTelemarketingRetrieve(tableData, setTableData, setSelected, setInitialSubs) {
  return (res) => {
    const newTableData = tableData;
    const subs = res.data?.subscriptions;

    if (subs) {
      setInitialSubs(subs);
      Object.values(subs).forEach((sub) => {
        if (sub?.type && sub?.status && typeMap[sub?.type] in tableData) {
          newTableData[typeMap[sub?.type]].value = sub.status;
        }
      });

      setTableData({
        ...tableData,
        ...newTableData
      });
    }
    setSelected([]);
  };
}

export function setupHandleSurveyRetrieve(tableData, setTableData, setSelected) {
  return (res) => {
    const newTableData = tableData;
    const subs = res.data?.subscriptions;
    const survey = subs?.some((item) => item.type === SURVEYS_TYPE);
    if (survey) {
      newTableData[typeMap[SURVEYS_TYPE]].value = subs?.find((item) => item.type === SURVEYS_TYPE).status;
      setTableData({ ...tableData, ...newTableData });
    }
    setSelected([]);
  };
}

const getPreferenceValue = (emails, preferenceCode) => {
  const preference = emails?.preferences?.preference?.find(
    (pref) => pref.code === preferenceCode
  );
  return preference ? preference.isSubscribed : null;
};

function extractEReceiptResponse(emails, setPrefChecked, sendInitial) {
  const eReceiptPref = emails ? getPreferenceValue(emails, ERECEIPTS) : null;
  const marketingPref = emails ? getPreferenceValue(emails, MARKETING_PREF) : null;
  const primaryFlag = emails?.primaryFlag;

  setPrefChecked(eReceiptPref);
  sendInitial({ eReceiptPref, primaryFlag, marketingPref });
}

export function setupEReceiptPreferenceUpdate(setEReceiptStatus, setInitialEReceipt) {
  return (res) => {
    const emails = res?.data?.profilePreferences?.profilePreference?.[0]?.emails?.[0];
    extractEReceiptResponse(emails, setEReceiptStatus, setInitialEReceipt);
  };
}

export function setupEReceiptPreferenceRetrieve(setEReceiptPrefChecked, sendInitialEReceipt) {
  return (res) => {
    const profileSet = res?.data?.accountProfile?.userProfile?.emails.find((item) => item.preferences);
    extractEReceiptResponse(profileSet, setEReceiptPrefChecked, sendInitialEReceipt);
  };
}

export function handleRollback(tableData, setTableData, selected, setSelected) {
  const newTableData = tableData;

  Object.values(selected).forEach((row) => {
    newTableData[row].value = tableData[row].value === NO ? YES : NO;
  });

  setTableData({
    ...tableData,
    ...newTableData
  });

  setSelected([]);
}

export function getUpdates(selected, table) {
  return selected.map((row) => table[row]);
}

export const convertStatusToValue = (status) => {
  return (status === YES) ? YES : NO;
};

export const getSubscriptions = (response) => {
  const res = response?.data;
  return res?.subscriptions || [];
};

export const convertSubsToPrefs = (tableData, subs) => {
  let table = tableData;

  subs.forEach((sub) => {
    const title = typeMap[sub.type];
    if (table[title]) {
      table[title].value = convertStatusToValue(sub.status);
      table[title].isPending = sub.status === PENDING;
    }
  });

  return table;
};

export const convertSmsStatusToValue = (status) => {
  return ((status === YES) || (status === PENDING)) ? YES : NO;
};

export const convertSmsSubsToPrefs = (tableData, subs, setSelected) => {
  let table = tableData;
  let selected = [];
  subs.forEach((sub) => {
    const title = typeMap[sub.type];
    if (table[title]) {
      table[title].value = convertSmsStatusToValue(sub.status);
      table[title].isPending = sub.status === PENDING;
    }
    selected.push(title);
  });
  setSelected(() => { return [...selected]; });
  return selected;
};

export function getUnsubscribeList(selected, tableData) {
  return selected
    .filter((sub) => tableData[sub].value === NO)
    .map((sub) => tableData[sub].type);
}

export function determineSuccessText(input, phoneNumber, email) {
  switch (input) {
  case 'text':
    return (
      <Typography variant="body-base">
        You are now receiving Order Status texts at&nbsp;
        <Typography component="b" weight="bold">
          {formatPhone(phoneNumber)}
        </Typography>
        . You are opted out from Order Status emails.
      </Typography>
    );
  case 'email':
    return (
      <Typography variant="body-base">
        You are now receiving Order Status emails at&nbsp;
        <Typography component="b" weight="bold">
          {email}
        </Typography>
        . You are opted out from Order Status texts.
      </Typography>
    );
  case 'both':
    return (
      <Typography variant="body-base">
        You are now receiving Order Status emails at&nbsp;
        <Typography component="b" weight="bold">
          {email}&nbsp;
        </Typography>
        and Order Status texts at&nbsp;
        <Typography component="b" weight="bold">
          {formatPhone(phoneNumber)}
        </Typography>
        .
      </Typography>
    );
  default:
    return null;
  }
}

export function determineEReceiptSuccessText(input, email) {
  switch (input) {
  case true:
    return (
      <Typography variant="body-base">
        <Typography component="b" weight="bold">
          {email}
        </Typography>
        {ERECEIPT_SUBSCRIBE_SUCCESS}
      </Typography>
    );
  case false:
    return (
      <Typography variant="body-base">
        <Typography component="b" weight="bold">
          {email}
        </Typography>
        {ERECEIPT_UNSUBSCRIBE_SUCCESS}
      </Typography>
    );
  default:
    return null;
  }
}

// Process results
export const newSubscription = (type, status) => {
  return {
    type,
    status
  };
};

export const resendText = (phoneNumber, table, callback, err) => {
  let preferences = Object.values(table).map((row) => {
    return row.isPending ? newSubscription(row.type, YES) : null;
  }).filter((row) => !!row);

  const request = {
    sourceId,
    phoneNumber,
    subscriptions: preferences
  };

  axiosPut({
    url: '/cdg/epc/v3/sms/update',
    data: request,
    callback,
    err
  });
};
